import { Sparkles } from "lucide-react";
import type * as React from "react";

/** Map `lucide-animated` export id (e.g. `HeartIcon`) to `lucide-react` icon name. */
export function lucideAnimatedIconIdToLucideReactKey(iconId: string): string {
	const t = iconId.trim();
	if (!t) {
		return "Sparkles";
	}
	return t.endsWith("Icon") ? t.slice(0, -4) : t;
}

/**
 * Static Lucide placeholder for `lucide_animated` (library mode) icons, shown until
 * `LucideAnimatedPublicHydrator` mounts the real animated icon client-side (both on
 * public pages and in the dashboard's own live "Preview", which renders this same
 * shared `renderBuilderNode` tree directly in the browser). Always renders `Sparkles`
 * rather than looking up the specific icon: a per-icon lookup needs either a wildcard
 * `import * as LucideIcons from "lucide-react"` (pulls the whole ~1,900-icon set into
 * the bundle — the original bug here) or a per-icon `import()` (only awaitable from a
 * genuine RSC/SSR render; the dashboard's Preview panel renders this tree as a plain
 * client component, where an async component throws instead of rendering — "A
 * component suspended while responding to synchronous input"). A single static
 * `Sparkles` import keeps this synchronous and safe in both places, and stays
 * tree-shakeable — `lucide-react`'s `sideEffects: false` means only this one icon
 * module ships either way.
 */
export function renderLucideAnimatedSsrFallback(params: {
	iconId: string;
	sizePx: number;
	color?: string;
	strokeWidth?: number;
}): React.ReactNode {
	const size = Math.max(12, Math.round(params.sizePx));
	const sw =
		typeof params.strokeWidth === "number" &&
		Number.isFinite(params.strokeWidth) &&
		params.strokeWidth > 0
			? params.strokeWidth
			: undefined;
	const color = params.color?.trim();
	const style: React.CSSProperties = color ? { color } : { opacity: 0.85 };
	return <Sparkles aria-hidden size={size} strokeWidth={sw} style={style} />;
}
